import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import {
  downloadArmAssignmentAllMetricsCsv,
  downloadArmAssignmentCsv,
  downloadArmAssignmentDesignDoc,
  fetchArmAssignmentProducts,
  fetchArmAssignmentReport,
} from '../services/arm-assignment-report.service';
import type {
  ArmAssignmentProductFilters,
  ArmAssignmentReport,
} from '../types/arm-assignment-report.types';

/** Poll cadence while the report is still being computed (422). */
const REPORT_POLL_INTERVAL_MS = 3_000;
/**
 * Soft cap: after this we stop treating a persistent 422 as "computing" and
 * surface the failed/empty card, so a genuinely uncomputable assignment (no
 * candidates / unresolvable metric) doesn't poll forever.
 */
const REPORT_SOFT_TIMEOUT_MS = 90_000;

/** Derived readiness for the page: still building, ready to render, or gave up. */
export type ArmReportPhase = 'computing' | 'ready' | 'failed';

/** Flips to `true` once the soft-timeout window elapses (drives the give-up transition). */
function useSoftTimeout(ms: number): boolean {
  const [deadline] = useState(() => Date.now() + ms);
  const [timedOut, setTimedOut] = useState(() => Date.now() >= deadline);
  useEffect(() => {
    if (timedOut) return;
    const handle = setTimeout(
      () => {
        setTimedOut(true);
      },
      Math.max(deadline - Date.now(), 0),
    );
    return () => {
      clearTimeout(handle);
    };
  }, [deadline, timedOut]);
  return timedOut;
}

/**
 * Polls the per-arm assignment report until it lands (200) or the soft timeout
 * elapses on a persistent 422. Returns the report plus a derived `phase` the
 * page uses to switch between the processing / failed / ready screens.
 */
export function useArmAssignmentReport(expId: string): {
  report: ArmAssignmentReport | undefined;
  phase: ArmReportPhase;
  error: unknown;
} {
  const softTimedOut = useSoftTimeout(REPORT_SOFT_TIMEOUT_MS);
  const query = useQuery({
    queryKey: queryKeys.analytics.experiments.armAssignmentReport(expId),
    queryFn: () => fetchArmAssignmentReport(expId),
    enabled: !!expId,
    // Keep polling only while the backend still returns 422 and we're within the
    // soft-timeout window; stop the moment it's ready or we've given up.
    refetchInterval: (q) =>
      q.state.data?.status === 'computing' && !softTimedOut ? REPORT_POLL_INTERVAL_MS : false,
  });

  const result = query.data;
  const phase: ArmReportPhase = query.isError
    ? 'failed'
    : result?.status === 'ready'
      ? 'ready'
      : softTimedOut && result?.status === 'computing'
        ? 'failed'
        : 'computing';

  return {
    report: result?.status === 'ready' ? result.report : undefined,
    phase,
    error: query.error,
  };
}

/**
 * A server-paginated page of assigned products for the given filters (arm /
 * segment / outliers / search / page / page_size), cached per filter combo.
 * `keepPreviousData` holds the prior page on screen while the next one loads so
 * paging/filtering doesn't flash an empty table.
 */
export function useArmAssignmentProducts(
  expId: string,
  filters: ArmAssignmentProductFilters,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.analytics.experiments.armAssignmentProducts(
      expId,
      filters as Record<string, unknown>,
    ),
    queryFn: () => fetchArmAssignmentProducts(expId, filters),
    enabled: !!expId && enabled,
    placeholderData: keepPreviousData,
  });
}

/** Mutation wrapping the CSV export so the button can show a spinner and toast on failure. */
export function useDownloadArmAssignmentCsv(expId: string) {
  return useMutation({
    mutationFn: () => downloadArmAssignmentCsv(expId),
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to download the CSV. Please try again.'));
    },
  });
}

/**
 * Mutation wrapping the all-metrics per-arm CSV export. Resolves the active
 * subsidiary's display name here (cross-store read at the hook layer) because the
 * service only receives it to name the downloaded file.
 */
export function useDownloadArmAssignmentAllMetricsCsv(expId: string) {
  const subsidiaryName = useAuthStore(
    (s) => s.user?.accessibleTenants.find((t) => t.id === s.user?.activeTenantId)?.name,
  );

  return useMutation({
    mutationFn: () => downloadArmAssignmentAllMetricsCsv(expId, subsidiaryName),
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to download the metrics CSV. Please try again.'));
    },
  });
}

/** Mutation wrapping the design-doc (.docx) export so the button can show a spinner and toast on failure. */
export function useDownloadArmAssignmentDesignDoc(expId: string) {
  return useMutation({
    mutationFn: () => downloadArmAssignmentDesignDoc(expId),
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to download the document. Please try again.'));
    },
  });
}
